import React, { useState, useEffect, useRef } from 'react';
import { X, Bell, MessageCircle, Users, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getNotifications, markNotificationsAsRead } from '@/lib/notifications';
import type { Notification } from '@/types/database';
import { cn } from '@/lib/utils';

interface NotificationsPopupProps {
  onClose: () => void;
  onNotificationsRead: () => void;
  onOpenMessage?: (conversationId: string) => void;
}

export function NotificationsPopup({ onClose, onNotificationsRead, onOpenMessage }: NotificationsPopupProps) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadNotifications();

    // Close dropdown when clicking outside
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadNotifications = async () => {
    const data = await getNotifications();
    setNotifications(data);
    setLoading(false);
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read
    if (!notification.read) {
      await markNotificationsAsRead([notification.id]);
      onNotificationsRead();
    }

    // Handle navigation based on notification type
    switch (notification.type) {
      case 'message':
        if (notification.conversation_id && onOpenMessage) {
          onOpenMessage(notification.conversation_id);
          onClose();
        }
        break;
      case 'post':
        if (notification.post_id) {
          onClose();
          navigate(`/posts/${notification.post_id}`);
        }
        break;
      case 'follow':
        if (notification.sender_id) {
          onClose();
          navigate(`/profile/${notification.sender_id}`);
        }
        break;
      case 'mention':
        if (notification.post_id) {
          onClose();
          navigate(`/posts/${notification.post_id}`);
        }
        break;
    }
  };

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'message':
        return MessageCircle;
      case 'follow':
        return Users;
      case 'post':
      case 'mention':
        return FileText;
      default:
        return Bell;
    }
  };

  return (
    <div 
      ref={dropdownRef}
      className="absolute right-0 mt-2 w-96 bg-white dark:bg-dark-paper rounded-xl shadow-xl overflow-hidden border border-gray-100 dark:border-dark-border animate-fade-in z-50"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b dark:border-dark-border bg-gray-50 dark:bg-dark-elevated">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Notifications</h2>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-200 dark:hover:bg-dark-paper rounded-lg transition-colors text-gray-600 dark:text-gray-400"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Content */}
      <div className="max-h-[calc(80vh-100px)] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-royal-purple"></div>
          </div>
        ) : notifications.length > 0 ? (
          <div className="divide-y dark:divide-dark-border">
            {notifications.map((notification) => {
              const Icon = getNotificationIcon(notification.type);
              return (
                <button
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={cn(
                    "w-full p-3 text-left hover:bg-gray-50 dark:hover:bg-dark-elevated transition-colors flex items-start gap-3",
                    !notification.read && "bg-royal-purple/5 dark:bg-royal-purple/10"
                  )}
                >
                  <div className={cn(
                    "p-1.5 rounded-full",
                    notification.read 
                      ? "bg-gray-100 dark:bg-dark-elevated" 
                      : "bg-royal-purple/10 dark:bg-royal-purple/20"
                  )}>
                    <Icon className={cn(
                      "w-4 h-4",
                      notification.read 
                        ? "text-gray-600 dark:text-gray-400" 
                        : "text-royal-purple"
                    )} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={cn(
                      "text-sm text-gray-900 dark:text-gray-100",
                      !notification.read && "font-medium"
                    )}>
                      {notification.title}
                    </p>
                    {notification.content && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5">
                        {notification.content}
                      </p>
                    )}
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      {new Date(notification.created_at).toLocaleDateString([], {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="py-8 text-center text-gray-500 dark:text-gray-400 text-sm">
            No notifications
          </div>
        )}
      </div>
    </div>
  );
}